import type { Xform } from "GenAlphaModel";
import type { Child } from "./child";
import type { Parent } from "./parent";
import type { DrawTo } from "../draw";
import type { RedrawRequest } from "../redraw";
import type { RenderContentBase } from "../content/base_render";
import { Cache, Table } from "../style";

//
// # (GUI) Entity
//
// Base class for many of most of the things in the GUI tree.
// Shapes, instances, layouts, etc are all made outta these.
//
// Just which things in the GUI are `Entity`s, and which don't need to be?
// Like in many such frameworks, kinda a judgment call.
// Thing should probably (or must be) `Entity`s if:
//
// - If you ever want the user to interact with them
//   - Dragging, clicking, editing text, etc
// - If they can have a different *highlighting state* from their parent
//   - E.g. the labels in an instance can be highlighted (and edited) separately from the complete instance
// - Maybe less intuitively: (at least often) if they have a different *style* from their parent
//   - This is more of a consequence of how our style system works than a fundamental rule.
//
export abstract class Entity<
  DataType,
  Root extends RenderContentBase = RenderContentBase
> implements Parent<Root>, Child
{
  //
  abstract redraw: (to: DrawTo) => unknown; // Re-draw with our existing data
  public data: DataType;
  readonly parent: Parent<Root>;
  readonly children = new Set<Child>(); // Child nodes

  constructor({ data, parent }: Args<DataType, Root>) {
    this.data = data;
    this.highlighted_ = parent.highlighted;
    this.parent = parent;
    this.parent.children.add(this); // Add ourselves to our parent's children

    //
    // NOTE: we *could* have a base-class constructor like this,
    // which calls the `build()` method inline.
    // This gets VERY confusing if `build` is an arrow-function,
    // because in that case, by the time this runs, `this` is still the base-class,
    // and you run the parent-class `build` method.
    // We could somehow enforce or disallow arrow-functions, but that's a pain.
    // Bottom line is if you need to call `build` at construction time,
    // sub-classes need to do it themselves.
    //
    // this.build();
    //
  }

  // (Style) Cache
  // The default/ base entity tracks a highlighting state,
  // but does not have a style - hence its `StyleType` is `unknown`.
  // Sub-classes generally override this with whatever type of
  // style object they need to store and pass to their drawing methods.
  readonly cache?: Cache<unknown> | undefined = undefined;

  // Utility method to create a style cache, attached to our entity.
  makeCache<T>(t: Table<T>): Cache<T> {
    return new Cache<T>(t, this);
  }

  // Highlighting state
  protected highlighted_: boolean;
  get highlighted(): boolean {
    return this.highlighted_;
  }

  highlight(state: boolean = true): RedrawRequest {
    this.highlighted_ = state; // Set highlighted state
    this.children.forEach((child) => {
      child.highlight(state);
    });
    return this.invalidate(false); // Invalidate our state
  }

  invalidate(hier: boolean = true): RedrawRequest {
    this.cache?.invalidate(); // Invalidate our state
    if (hier) {
      this.children.forEach((child) => {
        child.invalidate();
      });
    }
    return this.root.requestRedraw();
  }

  // Update
  // The core "change my data" API method.
  // Takes any partial subset of `DataType` and mixes it in.
  // Also calls the `clear` and `build` methods,
  // which generally set up child entities in compound ones.
  update(data: Partial<DataType>): RedrawRequest {
    this.clear();
    this.data = { ...this.data, ...data };
    this.build();
    return this.root.requestRedraw();
  }

  // Clear - for compound entities
  clear() {
    this.children.clear(); // Clear our children
  }

  // Build - for compound entities. No-op by default.
  build() {}

  // Default implementations of the `Parent` interface
  // Both recursively reach up-tree to find these properties.
  get root(): Root {
    return this.parent.root;
  }
  get xform(): Xform {
    return this.parent.xform;
  }
}

// Constructor arguments
export interface Args<
  DataType,
  Root extends RenderContentBase = RenderContentBase
> {
  data: DataType; // Input data
  parent: Parent<Root>; // Parent object
}
